'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import type { StudentApplication } from '@openclassrooms/sdk-web';
import useHelpTypeFromPathname, { type HelpType } from './useHelpTypeFromPathname';
import useDialog, { CurrentPage } from './useDialog';
import useAplOwner from './useAplOwner';
export type { CurrentPage };
export interface ContactContextType {
  helpType: HelpType;
  toggleHelpType: () => void;
  isDialogOpen: boolean;
  openDialog: (forcePage?: CurrentPage) => void;
  openEmail: (ownerEmail?: string, options?: {
    forced?: boolean;
  }) => void;
  closeDialog: (reason?: string) => void;
  currentPage: CurrentPage | undefined;
  goMainPage: () => void;
  changePage: (newPage: CurrentPage) => void;
  shouldDisplayBack: boolean;
  aplOwner: Omit<NonNullable<StudentApplication['owner']>, 'supportChannel'> | undefined;
  supportChannel: StudentApplication['supportChannel'];
  isOwnerLoading: boolean;
}
export const ContactContext = createContext<ContactContextType | null>(null);
export function ContactProvider(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  const initialHash = globalThis.location?.hash;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["#contact", "#contact-email"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isOpenDefault = t1.includes(initialHash);
  const [emailHashIntent, setEmailHashIntent] = useState(initialHash === "#contact-email");
  const {
    helpType,
    switchHelp
  } = useHelpTypeFromPathname();
  const {
    isDialogOpen,
    open: t2,
    openEmail,
    close: closeDialog,
    currentPage,
    goMainPage,
    changePage,
    shouldDisplayBack
  } = useDialog({
    isOpenDefault,
    onEmailHashClick: () => {
      setEmailHashIntent(true);
      openDialog();
    }
  });
  const openDialog = t2;
  const t3 = isDialogOpen || emailHashIntent;
  let t4;
  if ($[1] !== t3) {
    t4 = {
      enabled: t3
    };
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    aplOwner,
    supportChannel,
    isLoading: isOwnerLoading
  } = useAplOwner(t4);
  let t5;
  let t6;
  if ($[3] !== aplOwner || $[4] !== emailHashIntent || $[5] !== goMainPage || $[6] !== isOwnerLoading || $[7] !== openEmail) {
    t5 = () => {
      if (!emailHashIntent || isOwnerLoading) {
        return;
      }
      setEmailHashIntent(false);
      if (aplOwner?.email) {
        openEmail(aplOwner.email, {
          forced: true
        });
      } else {
        goMainPage();
      }
    };
    t6 = [emailHashIntent, isOwnerLoading, aplOwner, openEmail, goMainPage];
    $[3] = aplOwner;
    $[4] = emailHashIntent;
    $[5] = goMainPage;
    $[6] = isOwnerLoading;
    $[7] = openEmail;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== closeDialog || $[11] !== goMainPage || $[12] !== openDialog || $[13] !== switchHelp) {
    t7 = () => {
      closeDialog();
      setTimeout(() => {
        switchHelp();
        goMainPage();
        openDialog();
      }, 200);
    };
    $[10] = closeDialog;
    $[11] = goMainPage;
    $[12] = openDialog;
    $[13] = switchHelp;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const toggleHelpType = t7;
  let t8;
  if ($[15] !== aplOwner || $[16] !== changePage || $[17] !== closeDialog || $[18] !== currentPage || $[19] !== goMainPage || $[20] !== helpType || $[21] !== isDialogOpen || $[22] !== isOwnerLoading || $[23] !== openDialog || $[24] !== openEmail || $[25] !== shouldDisplayBack || $[26] !== supportChannel || $[27] !== toggleHelpType) {
    t8 = {
      helpType,
      toggleHelpType,
      isDialogOpen,
      openDialog,
      openEmail,
      closeDialog,
      currentPage,
      goMainPage,
      changePage,
      shouldDisplayBack,
      aplOwner,
      supportChannel,
      isOwnerLoading
    };
    $[15] = aplOwner;
    $[16] = changePage;
    $[17] = closeDialog;
    $[18] = currentPage;
    $[19] = goMainPage;
    $[20] = helpType;
    $[21] = isDialogOpen;
    $[22] = isOwnerLoading;
    $[23] = openDialog;
    $[24] = openEmail;
    $[25] = shouldDisplayBack;
    $[26] = supportChannel;
    $[27] = toggleHelpType;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const value = t8;
  let t9;
  if ($[29] !== children || $[30] !== value) {
    t9 = <ContactContext.Provider value={value}>{children}</ContactContext.Provider>;
    $[29] = children;
    $[30] = value;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}
export const useContactContext = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error("useContactContext must be used within a ContactProvider");
  }
  return context;
};